import { useState, type FormEvent } from "react";

// Replaces the HubSpot embed on /contact with a Web3Forms submission.
// Only this form is switched over — the footer "Feedback" form on every page is
// still the HubSpot embed (components/HubSpotForm.tsx).
//
// The access key is a client-side credential by design: Web3Forms puts it in
// the page for anyone to read. It is kept in NEXT_PUBLIC_WEB3FORMS_ACCESS_KEY
// so it is not committed to the repository, and the honeypot below is what
// actually keeps bots out.

const ACCESS_KEY = process.env.NEXT_PUBLIC_WEB3FORMS_ACCESS_KEY ?? "";

type Status = { kind: "idle" | "sending" | "ok" | "error"; message?: string };

export default function ContactForm() {
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    data.append("access_key", ACCESS_KEY);
    data.append("subject", "New enquiry from chestsurgeryindia.com");
    data.append("from_name", "Chest Surgery India website");

    setStatus({ kind: "sending" });

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: data,
      });
      const json = await res.json();

      if (res.ok && json.success) {
        form.reset();
        setStatus({
          kind: "ok",
          message: "Thank you — your message has been sent. We will be in touch shortly.",
        });
      } else {
        setStatus({
          kind: "error",
          message: json.message || "Your message could not be sent. Please try again.",
        });
      }
    } catch {
      setStatus({
        kind: "error",
        message:
          "Your message could not be sent. Please check your connection, or call +91 9540210956.",
      });
    }
  }

  if (!ACCESS_KEY) {
    // Better a visible note in development than a form that silently fails.
    return (
      <p className="contact-form__status contact-form__status--error">
        Online enquiries are temporarily unavailable. Please call +91 9540210956.
      </p>
    );
  }

  return (
    <form className="contact-form" onSubmit={onSubmit} noValidate={false}>
      {/* Web3Forms discards anything that fills this in. Hidden from people. */}
      <input type="checkbox" name="botcheck" className="contact-form__botcheck" tabIndex={-1} autoComplete="off" />

      <div className="contact-form__group">
        <label htmlFor="cf-first-name">
          First Name<span className="contact-form__required">*</span>
        </label>
        <input id="cf-first-name" name="First Name" type="text" required autoComplete="given-name" />
      </div>

      <div className="contact-form__group">
        <label htmlFor="cf-phone">
          Phone number<span className="contact-form__required">*</span>
        </label>
        <input id="cf-phone" name="Phone number" type="tel" required autoComplete="tel" />
      </div>

      <div className="contact-form__group">
        <label htmlFor="cf-city">City</label>
        <input id="cf-city" name="City" type="text" autoComplete="address-level2" />
      </div>

      <div className="contact-form__group">
        <label htmlFor="cf-message">Message</label>
        <textarea id="cf-message" name="Message" rows={4} />
      </div>

      <div className="contact-form__group">
        <label htmlFor="cf-email">
          Email<span className="contact-form__required">*</span>
        </label>
        <input id="cf-email" name="email" type="email" required autoComplete="email" />
      </div>

      <button type="submit" className="contact-form__submit" disabled={status.kind === "sending"}>
        {status.kind === "sending" ? "Sending..." : "Submit"}
      </button>

      {status.message && (
        <p
          className={`contact-form__status contact-form__status--${
            status.kind === "ok" ? "ok" : "error"
          }`}
          role="status"
          aria-live="polite"
        >
          {status.message}
        </p>
      )}
    </form>
  );
}
